/**
 * sessionReducer — pure QA-session state machine + storage-backed bootstrap.
 *
 * Extracted from QAPanel.tsx (mechanical split) so the reducer logic is
 * unit-testable without mounting the panel. Behavior pins live in
 * sessionReducer.test.ts: status cycling, auto-log-on-fail, and the
 * template-reconcile-on-load merge.
 */

import { storage } from '../../../services/StorageService';
import { buildFullGameQATemplate, buildSprintQATemplate } from './qaTemplates';
import { DebugFCT } from '../../../debug/DebugFCT';
import { gameLogger } from '../../../../utils/GameLogger';
import type { QASession, QAStatus } from './qaTypes';

// ── Reducer ──────────────────────────────────────────────

export type Action =
  | { type: 'TOGGLE_ITEM'; categoryId: string; itemId: string }
  | { type: 'SET_ITEM_NOTES'; categoryId: string; itemId: string; notes: string }
  | { type: 'SET_GLOBAL_NOTES'; notes: string }
  | { type: 'TOGGLE_SIGN_OFF' }
  | { type: 'RESET' }
  | { type: 'LOAD'; session: QASession };

export const nextStatus: Record<QAStatus, QAStatus> = {
  unchecked: 'pass',
  pass: 'fail',
  fail: 'unchecked',
};

export function sessionReducer(state: QASession, action: Action): QASession {
  const now = new Date().toISOString();
  switch (action.type) {
  case 'TOGGLE_ITEM':
    return {
      ...state,
      lastUpdated: now,
      categories: state.categories.map(cat =>
        cat.id !== action.categoryId ? cat : {
          ...cat,
          items: cat.items.map(item => {
            if (item.id !== action.itemId) return item;
            const newStatus = nextStatus[item.status];
            let notes = item.notes;
            // Auto-attach system logs when marking as fail
            if (newStatus === 'fail') {
              const fctLog = DebugFCT.copyHistory(20);
              const errLogs = (gameLogger.getLogs?.() || [])
                .filter((l: any) => l.level >= 4).slice(-10)
                .map((l: any) => `[${l.category}] ${l.message}`).join('\n');
              const autoLog = [fctLog, errLogs].filter(Boolean).join('\n---\n');
              if (autoLog) notes = (notes ? notes + '\n' : '') + `[AUTO-LOG ${now}]\n${autoLog}`;
            }
            return { ...item, status: newStatus, notes };
          }),
        }
      ),
    };
  case 'SET_ITEM_NOTES':
    return {
      ...state,
      lastUpdated: now,
      categories: state.categories.map(cat =>
        cat.id !== action.categoryId ? cat : {
          ...cat,
          items: cat.items.map(item =>
            item.id !== action.itemId ? item : { ...item, notes: action.notes }
          ),
        }
      ),
    };
  case 'SET_GLOBAL_NOTES':
    return { ...state, lastUpdated: now, globalNotes: action.notes };
  case 'TOGGLE_SIGN_OFF':
    return { ...state, lastUpdated: now, overallSignOff: !state.overallSignOff };
  case 'RESET':
    return {
      ...state,
      lastUpdated: now,
      overallSignOff: false,
      globalNotes: '',
      categories: state.categories.map(cat => ({
        ...cat,
        items: cat.items.map(item => ({ ...item, status: 'unchecked' as const, notes: '' })),
      })),
    };
  case 'LOAD':
    return action.session;
  default:
    return state;
  }
}

// ── Session bootstrap ────────────────────────────────────

export function createSession(template = buildFullGameQATemplate()): QASession {
  const now = new Date().toISOString();
  return {
    templateId: template.id,
    templateName: template.name,
    startedAt: now,
    lastUpdated: now,
    overallSignOff: false,
    globalNotes: '',
    categories: template.categories,
  };
}

export function loadOrCreateSession(buildTemplate = buildSprintQATemplate): QASession {
  const template = buildTemplate();
  const saved = storage.getObjectRaw<QASession>(`qa_session_${template.id}`);
  if (!saved) return createSession(template);

  // Reconcile: merge saved state with current template (handles added/removed items)
  const reconciledCategories = template.categories.map(tplCat => {
    const savedCat = saved.categories.find(c => c.id === tplCat.id);
    if (!savedCat) return tplCat;
    return {
      ...tplCat,
      items: tplCat.items.map(tplItem => {
        const savedItem = savedCat.items.find(i => i.id === tplItem.id);
        return savedItem ? { ...tplItem, status: savedItem.status, notes: savedItem.notes } : tplItem;
      }),
    };
  });

  return {
    ...saved,
    templateName: template.name,
    categories: reconciledCategories,
  };
}
